import React, { createContext, useContext, useState, useEffect } from 'react';

// Translation data
const translations = {
  en: {
    // Navigation
    home: 'Home',
    ai: 'AI',
    services: 'Services',
    team: 'Team',
    resources: 'Resources',
    support: 'Support',
    contact: 'Contact',
    login: 'Login',
    logout: 'Logout',
    continueTraining: 'Continue Training',
    loginToStart: 'Login to Start',
    
    // Homepage
    heroTitle: 'Empowering Every Child\'s Journey',
    heroSubtitle: 'AI-powered personalized support for children\'s development and learning',
    getStarted: 'Get Started',
    learnMore: 'Learn More',
    
    // Training pages
    progress: 'Progress',
    trainingPlan: 'Training Plan',
    dailyTask: 'Daily Task',
    backToHome: 'Back to Home',
    logOut: 'Log Out',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    errorBoundaryTitle: 'Oops! Something went wrong',
    errorBoundaryMessage: 'We\'re sorry, but something unexpected happened. Please try refreshing the page or go back to the home page.',
    refreshPage: 'Refresh Page',
    goHome: 'Go Home',
    errorDetails: 'Error Details (Development)',
    save: 'Save',
    cancel: 'Cancel',
    submit: 'Submit',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    
    // Language switcher
    switchToChinese: '中文',
    switchToEnglish: 'English',
    
    // Homepage Hero
    heroTitleLine1: 'Every Child Deserves',
    heroTitleLine2: 'Joyful Support',
    heroDescription: 'A beautiful private nature journal for writing and photos, with an optional community feed for sharing observations — inspired by iNaturalist and Instagram.',
    familiesSupported: 'Families Supported',
    learningGuides: 'Learning Guides',
    improvementSnapshots: 'Improvement Snapshots',
    positiveFeedback: 'Positive Feedback',
    startTraining: 'Start Training',
    meetOurTeam: 'Meet Our Team',
    child: 'Child',
    teacher: 'Teacher',
    therapist: 'Therapist',
    parent: 'Parent',
    learner: 'Learner',
    
    // Progress Page
    noChildAdded: 'No child information added yet',
    addChildInfo: 'Please add child information to view progress',
    addChildButton: 'Add Child Information',
    noTrainingPlan: 'No training plan yet',
    startFirstPlan: 'Start your first training plan to track your child\'s progress',
    createTrainingPlan: 'Create Training Plan',
    loadingProgress: 'Loading progress data...',
    yearsOld: 'years old',
    myActivePlans: 'My Active Plans',
    weeklyPlan: 'Weekly Plan',
    monthlyPlan: 'Monthly Plan',
    active: 'Active',
    completed: 'Completed',
    daysCompleted: 'days completed',
    viewPlan: 'View Plan',
    total: 'Total',
    latestScore: 'Latest Score',
    averageScore: 'Average',
    testSessions: 'Sessions',
    scoreTrend: 'Score Trend',
    dailyMilestones: 'Daily Milestones',
    testCompleted: 'Test Completed',
    day: 'Day',
    activities: 'activities',
    insufficientData: 'Keep going to unlock more insights. Complete 7 days to see improvement chart!',
    achievementMilestones: 'Achievement Milestones',
    startJourney: 'Start Journey',
    persist3Days: 'Persist 3 Days',
    persistWeek: 'Persist One Week',
    steadyProgress: 'Steady Progress',
    planNotFound: 'Plan ID not found',
    loadPlanFailed: 'Failed to load plan',
    planNotExist: 'Plan does not exist',
    comprehensive: 'Comprehensive',
    visitUs: 'Visit Us',
    visitAddress: 'Yunmei Baby Hub, Shenzhen',
    
    // Child Registration
    childInfoEntry: 'Child Information Entry',
    step1Label: 'Basic Information',
    step2Label: 'Condition Description',
    step1Subtitle: 'Please fill in your child\'s basic information. We will select the appropriate test based on age.',
    step2Subtitle: 'Please briefly describe your child\'s current condition and main problems to help us create a personalized training plan',
    childName: 'Child Name',
    childNamePlaceholder: 'Please enter child name',
    age: 'Age',
    agePlaceholder: 'Please enter child age',
    gender: 'Gender',
    selectGender: 'Please select',
    male: 'Male',
    female: 'Female',
    birthDate: 'Birth Date',
    parentName: 'Parent Name',
    parentNamePlaceholder: 'Please enter parent name',
    under6Info: 'Your child is under 6 years old. We will use age-appropriate testing methods (color recognition, shape matching, etc.) instead of number training.',
    over6Info: 'Your child is 6 years or older and will take the Schulte Grid attention test.',
    nextStep: 'Next: Describe Child Condition',
    mainProblems: 'Child\'s Main Problems',
    mainProblemsHint: 'Including normal children who want to improve grades',
    childCondition: 'Child Condition Description',
    conditionHint: '(Briefly describe your child\'s current condition, behavior, your observations, etc.)',
    conditionPlaceholder: 'For example: Child has difficulty concentrating, often gets distracted when reading or doing homework, likes to move around, emotions fluctuate easily...',
    submitAndStart: 'Submit: Start Test',
    
    // Training Plan Page
    overallProgress: 'Overall Progress',
    taskCompletionRate: 'Task Completion Rate',
    testCompletionRate: 'Test Completion Rate',
    improvementTrend: 'Improvement Trend',
    focusAreas: 'Focus Areas',
    attention: 'Attention',
    cognitive: 'Cognitive Ability',
    social: 'Social Skills',
    motor: 'Motor Skills',
    focusPreview: 'Training Goal Visualization',
    focusPreviewSubtitle: 'Overview of training focus and completion status for the first 7 days, helping parents know what is being trained today.',
    inProgress: 'In Progress',
    improvementSchedule: '7-Day Improvement Snapshots',
    improvementNote: 'The system automatically generates an improvement chart every 7 consecutive days completed, making progress visible.',
    chartGenerated: 'Chart Generated',
    completeToGenerate: 'Complete to Generate',
    trainingGoals: 'Training Goals',
    dailyTasks: 'Daily Tasks',
    pendingTest: 'Test Pending',
    
    // Daily Task Page
    taskNotFound: 'Task not found',
    loadTaskFailed: 'Failed to load task',
    taskMarkedComplete: 'Task marked as completed!',
    updateTaskFailed: 'Failed to update task, please try again',
    testCompletedRecorded: 'Test completed! Results recorded',
    trainingTarget: 'Training Target',
    trainingExpectation: 'Training Expectation',
    parentWish: 'Parent Wish',
    deadline: 'Deadline',
    aiCheer: 'AI Cheer',
    aiNote: 'After completing 7 days of tasks, we will automatically generate an improvement data chart to record every progress.',
    parentTime: 'Parent Time',
    parentTimeDescription: 'Record today\'s time spent with your child to help AI plan a more relaxed learning pace.',
    todayMinutes: 'Today (minutes)',
    independentTask: 'Do you want your child to complete some tasks independently (6+ years old)?',
    yesIndependent: 'Yes, can complete independently',
    noIndependent: 'No, needs parent accompaniment',
    childCompletes: 'Child completes: Mini games, matching tasks',
    parentSupport: 'Parent support: Prepare materials, give hugs after completion',
    parentCollaborate: 'Parent collaborates: Explain steps, accompany practice',
    childTries: 'Child tries: Final step completed independently by child',
    incompleteReason: 'Reason for Incomplete',
    incompleteReasonDescription: 'If the previous day\'s task was not completed, please briefly explain the reason. Our AI will generate a more considerate task for the next day.',
    incompleteReasonPlaceholder: 'For example: Child was sick and resting, went for a medical appointment, etc...',
    parentGuidance: 'Parent Guidance',
    parentGuidanceIntro: 'Please follow these steps to guide your child',
    todayActivities: 'Today\'s Activities',
    minutes: 'minutes',
    onlineGame: 'Online Game',
    markComplete: 'Mark Complete',
    detailedInstructions: 'Detailed Instructions:',
    startGame: 'Start Game',
    backToPlan: 'Back to Plan',
    todayFocus: 'Today\'s Focus',
    dailyTest: 'Daily Test',
    testDescription: 'After completing today\'s activities, please have your child complete the following test to record today\'s progress',
    startTest: 'Start Test',
    testCompletedExclamation: 'Test Completed!',
    score: 'Score',
    performanceLevel: 'Performance Level',
    excellent: 'Excellent',
    good: 'Good',
    average: 'Average',
    needsImprovement: 'Needs Improvement',
    markCompleteToday: 'Mark Today\'s Task Complete',
    dailyAIPraise: 'Daily AI Praise',
    aiPraiseNote: 'Remember to click the button above when done, we\'ll send new encouragement tomorrow! 💙',
    
    // Common problems
    attentionDeficit: 'Difficulty Concentrating',
    hyperactivity: 'Hyperactivity',
    moodSwings: 'Mood Swings',
    socialDifficulty: 'Social Difficulties',
    learningDifficulty: 'Learning Difficulties',
    languageDelay: 'Language Development Delay',
    behaviorIssues: 'Behavior Problems',
    poorCoordination: 'Poor Motor Coordination',
    poorMemory: 'Poor Memory',
    mathImprovement: 'Math Grades Need Improvement',
    chineseImprovement: 'Chinese Grades Need Improvement',
    englishImprovement: 'English Grades Need Improvement',
    lowEfficiency: 'Low Learning Efficiency',
    lackMotivation: 'Lack of Learning Motivation',
    slowHomework: 'Slow Homework Completion',
    weakUnderstanding: 'Weak Understanding Ability',
    other: 'Other',
    
    // HomePage additional
    progressFocus: 'Progress Focus',
    attentionLabel: 'Attention',
    cognitionLabel: 'Cognition',
    progressFocusDescription: 'The system automatically generates improvement charts every 7 days, allowing parents to see growth trajectories at a glance.',
    dailyCheer: 'Daily Cheer',
    dailyCheerQuote: 'Today\'s persistence is amazing! We\'ve completed new challenges together, keep smiling.',
    aiPositiveFeedback: 'AI Positive Feedback',
    dailyPositiveComment: 'Daily Positive Comment',
    goalTracker: 'Goal Tracker',
    todayTrainingArea: 'Today\'s training area: Attention · Visual Tracking',
    trainingExpectationExample: 'Training expectation: Complete puzzle with 5 minutes of focus',
    parentWishExample: 'Parent wish: Improve classroom focus within two weeks',
    goalTrackerDescription: 'Color coding makes it clear at a glance, helping parents plan the next actionable steps together.',
    familyRhythm: 'Family Rhythm',
    familyRhythmDescription: 'Record daily companionship time, system automatically adjusts task difficulty, making family rhythm more relaxed. All training games can be started directly on the website without download.',
    todayCompanionship: 'Today\'s companionship 45 minutes',
    independentPractice: 'Independent practice 20 minutes',
    whyTrustAI: 'Why You Can Trust Our AI',
    whyTrustAIDescription: 'Our AI system is designed with transparency, accuracy, and your child\'s best interests in mind',
    dataPrivacy: 'Data Privacy & Security',
    dataPrivacyDescription: 'Your child\'s information is protected with enterprise-grade encryption. We never share personal data with third parties and comply with all privacy regulations.',
    endToEndEncryption: 'End-to-end encryption',
    gdprCompliant: 'GDPR & COPPA compliant',
    noDataSharing: 'No data sharing with third parties',
    secureCloud: 'Secure cloud infrastructure',
    advancedAI: 'Advanced AI Technology',
    advancedAIDescription: 'Our AI is trained on extensive educational research and behavioral science data, ensuring accurate and evidence-based recommendations.',
    evidenceBased: 'Evidence-based algorithms',
    continuousLearning: 'Continuous learning & updates',
    multiDisciplinary: 'Multi-disciplinary approach',
    regularValidation: 'Regular accuracy validation',
    humanOversight: 'Human Oversight',
    humanOversightDescription: 'While AI provides analysis, our team of developers and learning guides continuously monitor and validate all recommendations.',
    learnerReview: 'Learner review process',
    qualityAssurance: 'Quality assurance checks',
    regularAudits: 'Regular system audits',
    userFeedback: 'User feedback integration',
    provenResults: 'Proven Results',
    provenResultsDescription: 'Our AI system has helped thousands of families with personalized recommendations that show measurable improvements in learning outcomes.',
    accuracyRate: '95% accuracy rate',
    positiveUserFeedback: 'Positive user feedback',
    measurableImprovements: 'Measurable improvements',
    ongoingOptimization: 'Ongoing optimization',
    transparencyCommitment: 'Transparency Commitment',
    transparencyDescription: 'We believe in complete transparency about how our AI works. You can always understand why specific recommendations are made and have full control over your data.',
    clearExplanations: 'Clear explanations for all recommendations',
    downloadData: 'Download your data anytime',
    modifyInfo: 'Modify or delete your information',
    askQuestions: 'Ask questions about our AI process',
    comprehensiveSupport: 'Comprehensive Support Services',
    comprehensiveSupportDescription: 'We provide a complete network of care professionals to support your child\'s unique needs and development',
    specialEducationTeachers: 'Special Education Teachers',
    specialEducationDescription: 'Certified special education professionals who create personalized learning plans and provide one-on-one instruction tailored to your child\'s learning style and abilities.',
    individualizedPlans: 'Individualized Education Plans (IEPs)',
    multiSensory: 'Multi-sensory learning approaches',
    progressMonitoring: 'Progress monitoring and assessment',
    parentTeacherCollab: 'Parent-teacher collaboration',
    occupationalTherapists: 'Occupational Therapists',
    occupationalDescription: 'Licensed therapists who help children develop essential life skills, motor coordination, and sensory processing abilities for daily activities and independence.',
    sensoryIntegration: 'Sensory integration therapy',
    motorDevelopment: 'Fine and gross motor development',
    dailyLivingSkills: 'Daily living skills training',
    adaptiveEquipment: 'Adaptive equipment recommendations',
    speechTherapists: 'Speech & Language Therapists',
    speechDescription: 'Specialized therapists who support communication development, language skills, and social interaction abilities for children with speech and language challenges.',
    communicationSkills: 'Communication skill development',
    socialInteraction: 'Social interaction training',
    alternativeCommunication: 'Alternative communication methods',
    familyCommunication: 'Family communication strategies',
    aiPoweredAnalysis: 'AI-Powered Analysis',
    aiAnalysisDescription: 'Advanced artificial intelligence that provides personalized educational recommendations and behavioral insights based on comprehensive data analysis.',
    behavioralAssessment: 'Intelligent behavioral assessment',
    personalizedRecommendations: 'Personalized learning recommendations',
    dataDrivenInsights: 'Data-driven insights and analysis',
    continuousAdaptation: 'Continuous learning and adaptation',
    familySupport: 'Family Support Specialists',
    familySupportDescription: 'Dedicated professionals who provide guidance, resources, and emotional support to families navigating the challenges of raising a special child.',
    parentEducation: 'Parent education and training',
    resourceCoordination: 'Resource coordination and advocacy',
    supportGroups: 'Support group facilitation',
    crisisSupport: 'Crisis support and intervention',
    careCoordination: '24/7 Care Coordination',
    careCoordinationDescription: 'Round-the-clock support system that ensures seamless communication between all care providers and immediate response to urgent needs.',
    emergencySupport: '24/7 emergency support line',
    careTeamCoordination: 'Care team coordination',
    progressTracking: 'Progress tracking and reporting',
    familyPlatform: 'Family communication platform',
    learnerJourneys: 'Learner Journeys',
    learnerJourneysDescription: 'We also focus on normal children, just want to improve their grades a little bit',
    readyToStart: 'Ready to Get Started?',
    readyToStartDescription: 'Take the first step toward comprehensive support for your child and family. Our team is here to help you navigate this journey with care, expertise, and compassion.',
    supportAvailable: '24/7 Support Available',
    confidentialSecure: 'Confidential & Secure',
    compassionateCare: 'Compassionate Care',
    beginJourney: 'Begin Your Journey',
    contactUs: 'Contact Us',
    supportLine: '24/7 Support Line',
    emailSupport: 'Email Support',
    professionalStandards: 'Professional Standards & References',
    standardsIntro: 'Our assessment questionnaires are developed based on internationally recognized professional standards and validated developmental screening tools to ensure accuracy and reliability.',
    viewStandards: 'View Standards',
    viewMilestones: 'View Milestones',
    assessmentNote: 'Our assessment tools incorporate elements from these validated instruments while being adapted for digital administration and enhanced with AI-powered analysis capabilities.',
    specialEducation: 'Special Education',
    occupationalTherapy: 'Occupational Therapy',
    speechTherapy: 'Speech Therapy',
    aiAnalysis: 'AI-Powered Analysis',
    educationalMaterials: 'Educational Materials',
    supportGroupsTitle: 'Support Groups',
    workshops: 'Workshops',
    familyResources: 'Family Resources',
    supportTitle: 'Support',
    helpline: '24/7 Helpline',
    emergencySupportTitle: 'Emergency Support',
    crisisIntervention: 'Crisis Intervention',
    familyGuidance: 'Family Guidance',
    contactInfo: 'Contact',
    contactMethod: 'Contact Method',
    natureJournal: 'Nature Journal',
    allRightsReserved: 'All rights reserved',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    accessibility: 'Accessibility',
    switchToChineseTitle: 'Switch to Chinese',
    switchToEnglishTitle: 'Switch to English',
    logoutConfirm: 'Are you sure you want to logout? Your session will be cleared.',
    
    // LoginPage
    welcomeBack: 'Welcome Back',
    signInDescription: 'Sign in to access your personalized support dashboard',
    emailAddress: 'Email Address',
    password: 'Password',
    enterEmail: 'Enter your email',
    enterPassword: 'Enter your password',
    signingIn: 'Signing In...',
    signIn: 'Sign In',
    dontHaveAccount: 'Don\'t have an account?',
    signUp: 'Sign Up',
    securePrivate: 'Secure & Private',
    secureDescription: 'Your child\'s information is protected with industry-standard security measures',
    hipaaCompliant: 'HIPAA compliant',
    secureStorage: 'Secure data storage',
    loginFailed: 'Login failed. Please try again.',
    
    // RegisterPage
    createAccount: 'Create Account',
    registerDescription: 'Receive cheerful, bilingual support for every learning goal',
    fullName: 'Full Name',
    enterFullName: 'Enter your full name',
    confirmPassword: 'Confirm Password',
    enterPasswordAgain: 'Confirm your password',
    passwordMinLength: 'Enter your password (min 6 characters)',
    creatingAccount: 'Creating Account...',
    signUpButton: 'Sign Up',
    alreadyHaveAccount: 'Already have an account?',
    welcomeJoin: 'Welcome',
    welcomeDescription: 'Thousands of families celebrate progress here',
    personalizedAssessments: 'Personalized assessments',
    professionalSupport: 'Professional support',
    trackProgress: 'Track progress',
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    passwordTooShort: 'Password must be at least 6 characters',
    passwordsDontMatch: 'Passwords do not match',
    registrationFailed: 'Registration failed. Please try again.',
    
    // TestPage
    childDevelopmentGame: 'Child Development Game Test',
    gameTestDescription: 'Select age-appropriate games to promote comprehensive child development',
    gamesCount: 'games',
    clickToEnter: 'Click to enter',
    gameInstructions: 'Game Instructions',
    age1to2: '1-2 years old',
    age1to2Desc: 'Focus on visual matching, develop basic cognitive abilities',
    age2to3: '2-3 years old',
    age2to3Desc: 'Learn color and shape recognition, build basic concepts',
    age3to4: '3-4 years old',
    age3to4Desc: 'Develop number recognition, cultivate mathematical thinking',
    age4to5: '4-5 years old',
    age4to5Desc: 'Improve cognitive abilities through memory games',
    age5to6: '5-6 years old',
    age5to6Desc: 'Learn emotion recognition, develop social skills',
    allGamesDesigned: 'All games are carefully designed to suit the developmental characteristics of the corresponding age group',
    gameResultsUsed: 'Game results will be used to assess children\'s development level and learning progress',
    visualCognition: 'Visual Cognition',
    colorShape: 'Color & Shape',
    numberRecognition: 'Number Recognition',
    memory: 'Memory',
    emotionSocial: 'Emotion & Social',
    comprehensiveDevelopment: 'Comprehensive Development',
    
    // OnlineGame
    backButton: '← Back',
    playLearn: 'Play & Learn',
    gameTagline: 'Ready to play directly on this site. No download needed',
    gameCompleted: 'Game Completed!',
    greatJob: 'Great Job!',
    aiComment: 'AI Comment',
    todayPerformance: 'Today\'s performance is amazing, keep going to see more progress! Keep shining! You\'re doing amazing! 🌟',
    scoreLabel: 'Score',
    timeLabel: 'Time',
    accuracyLabel: 'Accuracy',
    backToTasks: 'Back to Tasks',
    wrongTryAgain: 'Wrong, try again!',
    gameCompletedSimple: 'Game Completed!',
    scoreLabelSimple: 'Score',
    roundLabel: 'Round',
    findColor: 'Find color',
    findShape: 'Find shape',
    wrongOrder: 'Wrong order, try again!',
    viewSequence: 'View sequence',
    clickInOrder: 'Click colors in the order shown:',
    entered: 'Entered',
    puzzleCompleted: 'Puzzle Completed!',
    placed: 'Placed',
    selectPiece: 'Select puzzle piece:',
    memoryGameCompleted: 'Memory Game Completed!',
    matched: 'Matched',
    
    // ChildTestPage
    childAbilityTest: 'Child Ability Test',
    childTestDescription: 'Please have your child complete the following test. We will generate a personalized training plan based on the test results',
    schulteTest: 'Schulte Grid Test',
    schulteDescription: 'This is an attention test game. Please have your child click numbers in order from 1-25',
    testNote: 'Suitable for children 6 years and older',
    ageAppropriateTest: 'Age-Appropriate Cognitive Test',
    ageAppropriateDescription: 'We will assess cognitive abilities through games suitable for your child\'s age',
    ageAppropriateNote: 'Suitable for children under 6 years old',
    testCompletedTitle: 'Test Completed!',
    averageTime: 'Average Time',
    seconds: 'seconds',
    accuracy: 'Accuracy',
    completionTime: 'Completion Time',
    totalScore: 'Total Score',
    submitTestFailed: 'Failed to submit test results, please try again',
    generatePlan: 'Generate Training Plan',
    generatePlanFailed: 'Failed to generate plan, please try again',
    generatingPlan: 'Generating plan...',
    ageAdaptiveGameTest: 'Age-Adaptive Game Test',
    backToAgeSelection: 'Back to Age Selection',
    currentAge: 'Current Age',
    selectDifferentAges: 'Select different ages to test game adaptation effects',
    selectTestAge: 'Select Test Age',
    ageGroupInformation: 'Age Group Information',
    toddlerGroup: 'Toddler Group',
    kindergartenGroup: 'Kindergarten Group',
    elementaryGroup: 'Elementary Group',
    colorMatchingSoundGames: 'Color matching, sound games',
    shapeClassificationNumberGames: 'Shape classification, number games, memory matching',
    patternCompletionWordBuilding: 'Pattern completion, word building, logical reasoning',
    startGameTest: 'Start Game Test',
    testResults: 'Test Results',
    
    // TrainingPlanPage
    completePreviousDay: 'Please complete day {day} tasks first before continuing',
    
    // AssessmentPage
    languageDevelopmentAssessment: 'Language Development Assessment',
    assessLanguagePerformance: 'Please assess based on your child\'s language performance',
  },
  zh: {
    // Navigation
    home: '首页',
    ai: 'AI',
    services: '服务',
    team: '团队',
    resources: '资源',
    support: '支持',
    contact: '联系我们',
    login: '登录',
    logout: '登出',
    continueTraining: '继续训练计划',
    loginToStart: '登录开始',
    
    // Homepage
    heroTitle: '助力每个孩子的成长',
    heroSubtitle: 'AI驱动的个性化支持，帮助孩子发展和学习',
    getStarted: '开始使用',
    learnMore: '了解更多',
    
    // Training pages
    progress: '进度',
    trainingPlan: '训练计划',
    dailyTask: '每日任务',
    backToHome: '返回首页',
    logOut: '登出',
    
    // Common
    loading: '加载中...',
    error: '错误',
    success: '成功',
    errorBoundaryTitle: '糟糕！出错了',
    errorBoundaryMessage: '很抱歉，发生了意外情况。请尝试刷新页面或返回首页。',
    refreshPage: '刷新页面',
    goHome: '返回首页',
    errorDetails: '错误详情（开发模式）',
    save: '保存',
    cancel: '取消',
    submit: '提交',
    back: '返回',
    next: '下一步',
    previous: '上一步',
    close: '关闭',
    confirm: '确认',
    yes: '是',
    no: '否',
    
    // Language switcher
    switchToChinese: '中文',
    switchToEnglish: 'English',
    
    // Homepage Hero
    heroTitleLine1: '每个孩子都值得',
    heroTitleLine2: '快乐学习的陪伴',
    heroDescription: '云美宝宝学习站汇聚老师、治疗师与家长，共同打造既有趣又安全的双语训练旅程。无论是需要额外关怀的孩子，还是只是想提升成绩的普通孩子，我们都以积极的口吻给予鼓励与反馈。',
    familiesSupported: '家庭获益',
    learningGuides: '学习引导师',
    improvementSnapshots: '七日可视图',
    positiveFeedback: '正向反馈',
    startTraining: '开始训练',
    meetOurTeam: '认识我们的团队',
    child: '孩子',
    teacher: '老师',
    therapist: '治疗师',
    parent: '家长',
    learner: '学习者',
    
    // Progress Page
    noChildAdded: '还没有添加孩子信息',
    addChildInfo: '请先添加孩子信息以查看进度',
    addChildButton: '添加孩子信息',
    noTrainingPlan: '还没有训练计划',
    startFirstPlan: '开始第一个训练计划以追踪孩子的进步',
    createTrainingPlan: '创建训练计划',
    loadingProgress: '加载进度数据中...',
    yearsOld: '岁',
    myActivePlans: '我的训练计划',
    weeklyPlan: '一周计划',
    monthlyPlan: '一个月计划',
    active: '进行中',
    completed: '已完成',
    daysCompleted: '天完成',
    viewPlan: '查看计划',
    total: '共',
    latestScore: '最近分数',
    averageScore: '平均分数',
    testSessions: '测试次数',
    scoreTrend: '测试分数趋势',
    dailyMilestones: '每日完成情况',
    testCompleted: '测试已完成',
    day: '第',
    activities: '个活动',
    insufficientData: '数据不足，继续完成任务可获得更多分析',
    achievementMilestones: '成就里程碑',
    startJourney: '开始旅程',
    persist3Days: '坚持3天',
    persistWeek: '坚持一周',
    steadyProgress: '稳定进步',
    planNotFound: '未找到计划ID',
    loadPlanFailed: '加载计划失败',
    planNotExist: '计划不存在',
    comprehensive: '综合能力',
    visitUs: '访问我们',
    visitAddress: '云美宝宝成长中心 · 深圳',
    
    // Child Registration
    childInfoEntry: '孩子信息录入',
    step1Label: '基本信息',
    step2Label: '状况说明',
    step1Subtitle: '请填写孩子的基本信息，我们将根据年龄选择合适的测试',
    step2Subtitle: '请简单说明孩子的现状和主要问题，帮助我们制定个性化训练计划',
    childName: '孩子姓名',
    childNamePlaceholder: '请输入孩子姓名',
    age: '年龄',
    agePlaceholder: '请输入孩子年龄',
    gender: '性别',
    selectGender: '请选择',
    male: '男',
    female: '女',
    birthDate: '出生日期',
    parentName: '家长姓名',
    parentNamePlaceholder: '请输入家长姓名',
    under6Info: '您的孩子未满6岁，我们将使用适合低龄儿童的测试方式（颜色识别、形状匹配等），而不是数字训练。',
    over6Info: '您的孩子已满6岁，将进行舒尔特方格注意力测试。',
    nextStep: '下一步：说明孩子状况',
    mainProblems: '孩子当前的主要问题',
    mainProblemsHint: '包括正常孩子想提升成绩的问题',
    childCondition: '孩子状况说明',
    conditionHint: '（简单描述孩子的现状、行为表现、您的观察等）',
    conditionPlaceholder: '例如：孩子平时注意力很难集中，看书或做作业时经常分心，喜欢到处走动，情绪容易波动...',
    submitAndStart: '完成：开始测试',
    
    // Training Plan Page
    overallProgress: '总体进度',
    taskCompletionRate: '任务完成率',
    testCompletionRate: '测试完成率',
    improvementTrend: '改善趋势',
    focusAreas: '重点改善领域',
    attention: '注意力',
    cognitive: '认知能力',
    social: '社交能力',
    motor: '运动能力',
    focusPreview: '训练目标可视化',
    focusPreviewSubtitle: '前七天的训练重点与完成状态一览，帮助家长知道今天训练了哪个地方。',
    inProgress: '进行中',
    improvementSchedule: '改善数据图安排',
    improvementNote: '系统会在每连续完成七天任务后自动生成一张改善图，让正向进步更直观。',
    chartGenerated: '改善图已生成',
    completeToGenerate: '完成本阶段后生成',
    trainingGoals: '训练目标',
    dailyTasks: '每日任务',
    pendingTest: '待测试',
    
    // Daily Task Page
    taskNotFound: '任务不存在',
    loadTaskFailed: '加载任务失败',
    taskMarkedComplete: '任务标记为已完成！',
    updateTaskFailed: '更新任务失败，请重试',
    testCompletedRecorded: '测试完成！结果已记录',
    trainingTarget: '训练目标',
    trainingExpectation: '训练预期',
    parentWish: '家长期盼',
    deadline: '目标期限',
    aiCheer: 'AI 鼓励',
    aiNote: '完成 7 天任务后，我们会自动生成改善数据图，记录每一次进步。',
    parentTime: '家长陪伴时长',
    parentTimeDescription: '记录今天陪伴的时间，帮助 AI 规划更轻松的学习节奏。',
    todayMinutes: '今日陪伴（分钟）',
    independentTask: '是否希望孩子独立完成部分任务（6 岁以上）?',
    yesIndependent: '可以独立完成',
    noIndependent: '需要家长陪同',
    childCompletes: '孩子自己完成：小游戏、配对任务',
    parentSupport: '家长支持：准备材料、结束后给拥抱',
    parentCollaborate: '家长协作：讲解步骤、陪同练习',
    childTries: '孩子尝试：最后一步由孩子独立完成',
    incompleteReason: '未完成原因',
    incompleteReasonDescription: '如果前一天未完成任务，请简单说明原因，我们的 AI 会生成更贴心的下一天任务。',
    incompleteReasonPlaceholder: '例如：孩子生病休息、外出就诊等……',
    parentGuidance: '家长指导',
    parentGuidanceIntro: '请按照以下步骤指导孩子完成活动',
    todayActivities: '今日活动',
    minutes: '分钟',
    onlineGame: '在线游戏',
    markComplete: '标记完成',
    detailedInstructions: '详细操作步骤：',
    startGame: '开始游戏',
    backToPlan: '返回计划',
    todayFocus: '今日强化',
    dailyTest: '每日测试',
    testDescription: '完成今日活动后，请让孩子完成以下测试，以记录今天的进步',
    startTest: '开始测试',
    testCompletedExclamation: '测试已完成！',
    score: '得分',
    performanceLevel: '表现水平',
    excellent: '优秀',
    good: '良好',
    average: '一般',
    needsImprovement: '需要改进',
    markCompleteToday: '标记今日任务完成',
    dailyAIPraise: 'AI 今日评语',
    aiPraiseNote: '完成后记得点击上方按钮，我们会在明天送上新的鼓励。💙',
    
    // Common problems
    attentionDeficit: '注意力不集中',
    hyperactivity: '多动',
    moodSwings: '情绪波动大',
    socialDifficulty: '社交困难',
    learningDifficulty: '学习困难',
    languageDelay: '语言发育迟缓',
    behaviorIssues: '行为问题',
    poorCoordination: '运动协调性差',
    poorMemory: '记忆力差',
    mathImprovement: '数学成绩需要提升',
    chineseImprovement: '语文成绩需要提升',
    englishImprovement: '英语成绩需要提升',
    lowEfficiency: '学习效率低',
    lackMotivation: '缺乏学习动力',
    slowHomework: '作业完成慢',
    weakUnderstanding: '理解能力需加强',
    other: '其他',
    
    // HomePage additional
    progressFocus: '进步亮点',
    attentionLabel: '注意力',
    cognitionLabel: '认知力',
    progressFocusDescription: '系统每七天自动生成改善图表，让家长一眼看见成长轨迹。',
    dailyCheer: '每日鼓励',
    dailyCheerQuote: '今天的坚持真棒！我们已经一起完成了新的挑战，继续保持笑容。',
    aiPositiveFeedback: 'AI Positive Feedback',
    dailyPositiveComment: '每日正向评语',
    goalTracker: '训练目标看得见',
    todayTrainingArea: '今日训练部位：注意力 · 视觉追踪',
    trainingExpectationExample: '训练预期：集中 5 分钟完成拼图',
    parentWishExample: '家长期盼：两周内提升课堂专注力',
    goalTrackerDescription: '颜色标记一目了然，帮助家长一起制定可执行的下一步。',
    familyRhythm: '家庭节奏',
    familyRhythmDescription: '记录每日陪伴时长，系统自动调整任务难度，让家庭节奏更轻松，所有训练游戏都能在网站直接开启，无需下载。',
    todayCompanionship: '今日陪伴 45 分钟',
    independentPractice: '独立练习 20 分钟',
    whyTrustAI: '为什么您可以信任我们的AI',
    whyTrustAIDescription: '我们的AI系统以透明度、准确性和您孩子的最佳利益为设计理念',
    dataPrivacy: '数据隐私与安全',
    dataPrivacyDescription: '您孩子的信息受到企业级加密保护。我们从不与第三方共享个人数据，并遵守所有隐私法规。',
    endToEndEncryption: '端到端加密',
    gdprCompliant: '符合GDPR和COPPA标准',
    noDataSharing: '不与第三方共享数据',
    secureCloud: '安全的云基础设施',
    advancedAI: '先进的AI技术',
    advancedAIDescription: '我们的AI基于广泛的教育研究和行为科学数据进行训练，确保提供准确且基于证据的建议。',
    evidenceBased: '基于证据的算法',
    continuousLearning: '持续学习与更新',
    multiDisciplinary: '多学科方法',
    regularValidation: '定期准确性验证',
    humanOversight: '人工监督',
    humanOversightDescription: '虽然AI提供分析，但我们的开发团队和学习引导师持续监督并验证所有建议。',
    learnerReview: '学习者审核流程',
    qualityAssurance: '质量保证检查',
    regularAudits: '定期系统审核',
    userFeedback: '用户反馈整合',
    provenResults: '经过验证的结果',
    provenResultsDescription: '我们的AI系统已帮助数千个家庭提供个性化建议，在学习成果方面显示出可衡量的改善。',
    accuracyRate: '95%准确率',
    positiveUserFeedback: '积极的用户反馈',
    measurableImprovements: '可衡量的改善',
    ongoingOptimization: '持续优化',
    transparencyCommitment: '透明度承诺',
    transparencyDescription: '我们相信完全透明地说明AI的工作原理。您始终可以理解为什么提出特定建议，并完全控制您的数据。',
    clearExplanations: '所有建议的清晰解释',
    downloadData: '随时下载您的数据',
    modifyInfo: '修改或删除您的信息',
    askQuestions: '询问我们的AI流程',
    comprehensiveSupport: '综合支持服务',
    comprehensiveSupportDescription: '我们提供完整的护理专业人员网络，支持您孩子的独特需求和发展',
    specialEducationTeachers: '特殊教育教师',
    specialEducationDescription: '经过认证的特殊教育专业人员，创建个性化学习计划，并根据您孩子的学习风格和能力提供一对一指导。',
    individualizedPlans: '个性化教育计划（IEP）',
    multiSensory: '多感官学习方法',
    progressMonitoring: '进度监控和评估',
    parentTeacherCollab: '家长-教师协作',
    occupationalTherapists: '职业治疗师',
    occupationalDescription: '持牌治疗师，帮助儿童发展基本生活技能、运动协调和感觉处理能力，以进行日常活动和独立生活。',
    sensoryIntegration: '感觉统合治疗',
    motorDevelopment: '精细和粗大运动发展',
    dailyLivingSkills: '日常生活技能训练',
    adaptiveEquipment: '适应性设备建议',
    speechTherapists: '言语和语言治疗师',
    speechDescription: '专业治疗师，支持有言语和语言挑战的儿童的沟通发展、语言技能和社交互动能力。',
    communicationSkills: '沟通技能发展',
    socialInteraction: '社交互动训练',
    alternativeCommunication: '替代沟通方法',
    familyCommunication: '家庭沟通策略',
    aiPoweredAnalysis: 'AI驱动的分析',
    aiAnalysisDescription: '先进的人工智能，基于综合分析提供个性化教育建议和行为洞察。',
    behavioralAssessment: '智能行为评估',
    personalizedRecommendations: '个性化学习建议',
    dataDrivenInsights: '数据驱动的洞察和分析',
    continuousAdaptation: '持续学习和适应',
    familySupport: '家庭支持专家',
    familySupportDescription: '致力于为抚养特殊儿童的家庭提供指导、资源和情感支持的专业人员。',
    parentEducation: '家长教育和培训',
    resourceCoordination: '资源协调和倡导',
    supportGroups: '支持小组促进',
    crisisSupport: '危机支持和干预',
    careCoordination: '24/7护理协调',
    careCoordinationDescription: '全天候支持系统，确保所有护理提供者之间的无缝沟通，并立即响应紧急需求。',
    emergencySupport: '24/7紧急支持热线',
    careTeamCoordination: '护理团队协调',
    progressTracking: '进度跟踪和报告',
    familyPlatform: '家庭沟通平台',
    learnerJourneys: '孩子故事',
    learnerJourneysDescription: '我们同样关注普通孩子，只是想把成绩再提升一点点',
    readyToStart: '准备开始了吗？',
    readyToStartDescription: '迈出为您的孩子和家庭提供全面支持的第一步。我们的团队将用关怀、专业和同情心帮助您完成这段旅程。',
    supportAvailable: '24/7支持可用',
    confidentialSecure: '保密和安全',
    compassionateCare: '富有同情心的护理',
    beginJourney: '开始您的旅程',
    contactUs: '联系我们',
    supportLine: '24小时支持热线',
    emailSupport: '邮件支持',
    professionalStandards: '专业标准和参考文献',
    standardsIntro: '我们的评估问卷基于国际认可的专业标准和经过验证的发展筛查工具开发，以确保准确性和可靠性。',
    viewStandards: '查看标准',
    viewMilestones: '查看里程碑',
    assessmentNote: '我们的评估工具融合了这些经过验证的工具的元素，同时适应数字管理并通过AI驱动的分析功能得到增强。',
    specialEducation: '特殊教育',
    occupationalTherapy: '职业治疗',
    speechTherapy: '言语治疗',
    aiAnalysis: 'AI驱动的分析',
    educationalMaterials: '教育材料',
    supportGroupsTitle: '支持小组',
    workshops: '工作坊',
    familyResources: '家庭资源',
    supportTitle: '支持',
    helpline: '24/7帮助热线',
    emergencySupportTitle: '紧急支持',
    crisisIntervention: '危机干预',
    familyGuidance: '家庭指导',
    contactInfo: '联系方式',
    contactMethod: '联系方式',
    natureJournal: '自然日记',
    allRightsReserved: '版权所有',
    privacyPolicy: '隐私政策',
    termsOfService: '服务条款',
    accessibility: '可访问性',
    switchToChineseTitle: '切换到中文',
    switchToEnglishTitle: '切换到英文',
    logoutConfirm: '您确定要登出吗？您的会话将被清除。',
    
    // LoginPage
    welcomeBack: '欢迎回来',
    signInDescription: '登录以访问您的个性化支持仪表板',
    emailAddress: '邮箱地址',
    password: '密码',
    enterEmail: '请输入您的邮箱',
    enterPassword: '请输入您的密码',
    signingIn: '登录中...',
    signIn: '登录',
    dontHaveAccount: '还没有账号？',
    signUp: '注册',
    securePrivate: '安全与隐私',
    secureDescription: '您孩子的信息受到行业标准安全措施的保护',
    hipaaCompliant: '符合HIPAA标准',
    secureStorage: '安全数据存储',
    loginFailed: '登录失败，请重试。',
    
    // RegisterPage
    createAccount: '注册成为学习伙伴',
    registerDescription: '用中英双语记录孩子的每一次小进步',
    fullName: '姓名',
    enterFullName: '请输入您的姓名',
    confirmPassword: '再次输入密码',
    enterPasswordAgain: '请再次输入密码',
    passwordMinLength: '请输入密码（至少6个字符）',
    creatingAccount: '创建账户中...',
    signUpButton: '正式加入',
    alreadyHaveAccount: '已经有账号吗？',
    welcomeJoin: '欢迎加入',
    welcomeDescription: '每天收到 AI 的正向反馈',
    personalizedAssessments: '个性化评估',
    professionalSupport: '专业引导',
    trackProgress: '进步随时看',
    nameRequired: '姓名是必填项',
    emailRequired: '邮箱是必填项',
    passwordRequired: '密码是必填项',
    passwordTooShort: '密码必须至少6个字符',
    passwordsDontMatch: '密码不匹配',
    registrationFailed: '注册失败，请重试。',
    
    // TestPage
    childDevelopmentGame: '儿童发展游戏测试',
    gameTestDescription: '根据年龄段选择适合的游戏，促进儿童全面发展',
    gamesCount: '个游戏',
    clickToEnter: '点击进入',
    gameInstructions: '游戏说明',
    age1to2: '1-2岁',
    age1to2Desc: '专注于视觉匹配，培养基础认知能力',
    age2to3: '2-3岁',
    age2to3Desc: '学习颜色和形状识别，建立基础概念',
    age3to4: '3-4岁',
    age3to4Desc: '发展数字认知，培养数学思维',
    age4to5: '4-5岁',
    age4to5Desc: '通过记忆游戏提升认知能力',
    age5to6: '5-6岁',
    age5to6Desc: '学习情绪识别，发展社交技能',
    allGamesDesigned: '所有游戏都经过精心设计，适合相应年龄段的发展特点',
    gameResultsUsed: '游戏结果将用于评估儿童的发展水平和学习进度',
    visualCognition: '视觉认知',
    colorShape: '颜色形状',
    numberRecognition: '数字认知',
    memory: '记忆力',
    emotionSocial: '情绪社交',
    comprehensiveDevelopment: '综合发展',
    
    // OnlineGame
    backButton: '← 返回',
    playLearn: '在线游戏',
    gameTagline: '在这里直接开始游戏练习，轻松又安全，无需下载',
    gameCompleted: '游戏完成！',
    greatJob: '太棒了！',
    aiComment: 'AI 评语',
    todayPerformance: '今天的表现超棒，坚持就能看到更多进步哦！Keep shining! You\'re doing amazing! 🌟',
    scoreLabel: '得分',
    timeLabel: '完成时间',
    accuracyLabel: '准确率',
    backToTasks: '返回任务',
    wrongTryAgain: '不对，再试一次！',
    gameCompletedSimple: '游戏完成！',
    scoreLabelSimple: '得分',
    roundLabel: '轮',
    findColor: '找到颜色',
    findShape: '找到形状',
    wrongOrder: '顺序不对，再试一次！',
    viewSequence: '查看序列',
    clickInOrder: '按照刚才的顺序点击颜色：',
    entered: '已输入',
    puzzleCompleted: '拼图完成！',
    placed: '已放置',
    selectPiece: '选择拼图片：',
    memoryGameCompleted: '记忆游戏完成！',
    matched: '已匹配',
    
    // ChildTestPage
    childAbilityTest: '孩子能力测试',
    childTestDescription: '请让孩子完成以下测试，我们将根据测试结果生成个性化的训练计划',
    schulteTest: '舒尔特方格测试',
    schulteDescription: '这是一个注意力测试游戏，请让孩子按照1-25的顺序点击数字',
    testNote: '适合6岁以上儿童',
    ageAppropriateTest: '适合低龄儿童的认知测试',
    ageAppropriateDescription: '我们将通过适合您孩子年龄的游戏来评估认知能力',
    ageAppropriateNote: '适合6岁以下儿童',
    testCompletedTitle: '测试完成！',
    averageTime: '平均用时',
    seconds: '秒',
    accuracy: '准确率',
    completionTime: '完成时间',
    totalScore: '综合得分',
    submitTestFailed: '提交测试结果失败，请重试',
    generatePlan: '生成训练计划',
    generatePlanFailed: '生成计划失败，请重试',
    generatingPlan: '正在生成计划...',
    ageAdaptiveGameTest: '年龄适配游戏测试',
    backToAgeSelection: '返回年龄选择',
    currentAge: '当前年龄',
    selectDifferentAges: '选择不同年龄以测试游戏适配效果',
    selectTestAge: '选择测试年龄',
    ageGroupInformation: '年龄组信息',
    toddlerGroup: '幼儿组',
    kindergartenGroup: '幼儿园组',
    elementaryGroup: '小学组',
    colorMatchingSoundGames: '颜色匹配、声音游戏',
    shapeClassificationNumberGames: '形状分类、数字游戏、记忆匹配',
    patternCompletionWordBuilding: '图案完成、词汇构建、逻辑推理',
    startGameTest: '开始游戏测试',
    testResults: '测试结果',
    
    // TrainingPlanPage
    completePreviousDay: '请先完成第 {day} 天的任务再继续。',
    
    // AssessmentPage
    languageDevelopmentAssessment: '语言发展评估',
    assessLanguagePerformance: '请根据您孩子的语言表现进行评估',
  }
};

// Create context
const LanguageContext = createContext();

// Language provider component
export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get language from localStorage or default to 'en'
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'zh' : 'en');
  };

  const t = (key) => {
    return translations[language]?.[key] || key;
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation function for use outside components
export const getTranslation = (key, lang = null) => {
  const currentLang = lang || localStorage.getItem('language') || 'en';
  return translations[currentLang]?.[key] || key;
};

